import React from 'react';
import { setLightness, transparentize } from 'polished';

import { useSession } from '../context';
import CloseButton from './CloseButton';
import {
  SITE_SLUG,
  chatWindowHeaderHeight,
  chatWindowHeaderShadowHeight,
} from '../consts';
import { ChatWindowIcon } from './ChatWindowIcon';

const ChatWindowHeader = React.memo(() => {
  const { site, conversation } = useSession();
  const { botConfig, companyName } = site;
  const { testMode, useHumanAgent, liveAgentDisplayName } = conversation.value;

  const isTesting = Boolean(testMode);
  /** Human will answer chats, chatbot isn't active */
  const isHuman =
    useHumanAgent ||
    conversation.value.conversationMode === 'liveChatSMS' ||
    conversation.value.conversationMode === 'liveChatWeb';

  let botIsLive = true;

  if (botConfig.buildStages) {
    const currentStage = botConfig.buildStages.find(
      stage => stage.status === 'current'
    );

    if (currentStage && currentStage.id !== 'launch') {
      botIsLive = false;
    }
  }

  /** Display name show in Chat Header is Company Name if human is expected to answer,
   * or Bot Name if chatbot is expected to reply.
   *
   * If human has accepted the chat (Webchat), the live chat agent name will be shown */
  let displayName =
    companyName ||
    SITE_SLUG.split('-')
      .map(word => word[0].toUpperCase() + word.substring(1))
      .join(' ');

  if (isHuman && liveAgentDisplayName) {
    // use live agent display name if available
    displayName = liveAgentDisplayName;
  } else if (!isHuman) {
    // use bot name chatbot is expected to reply
    displayName = botConfig.botName;
  }

  return (
    <div>
      <div
        css={(theme: Theme) => ({
          position: 'relative',
          flex: '0 0 53px',
          backgroundColor: theme.colors.primary,
          borderRadius: '4px 4px 0 0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingLeft: 15,
          paddingRight: 10,
          height: chatWindowHeaderHeight,
        })}
      >
        <div
          css={(theme: Theme) => ({
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            height: chatWindowHeaderShadowHeight,
            backgroundColor: transparentize(0.7, theme.colors.primary),
          })}
        />
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <div
            css={{
              position: 'relative',
              flex: '0 0 60px',
              width: 60,
              height: 60,
              lineHeight: 0,
            }}
          >
            <ChatWindowIcon />
          </div>

          <div css={{ flex: 1, marginLeft: 10 }}>
            <h3
              css={{
                color: 'white',
                fontSize: 16,
                marginBottom: 2,
              }}
            >
              {displayName}
            </h3>
            <h5
              css={(theme: Theme) => ({
                color: setLightness(0.85, theme.colors.primary),
                fontSize: 12,
                fontWeight: 300,
                letterSpacing: 1,
                display: 'flex',
                alignItems: 'center',
              })}
            >
              <span
                css={(theme: Theme) => ({
                  display: 'inline-block',
                  backgroundColor: theme.colors.greenBright,
                  boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.35)',
                  borderRadius: '50%',
                  width: 7,
                  height: 7,
                  marginRight: 5,
                })}
              />
              {isHuman ? 'Live Chat' : 'AI Chatbot'}
            </h5>
          </div>
        </div>
        <CloseButton />
      </div>
      {isTesting && (
        <div style={{ backgroundColor: 'white' }}>
          <div
            css={(theme: Theme) => ({
              backgroundColor: transparentize(0.65, theme.colors.primary),
              fontSize: '14px',
              fontStyle: 'italic',
              padding: '8px',
              textAlign: 'center',
            })}
          >
            {botIsLive
              ? 'TESTING... chat will not appear in logs'
              : 'TESTING... bot is not live yet'}
          </div>
        </div>
      )}
    </div>
  );
});

export default ChatWindowHeader;
